export type SafeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'clear'>;

const createMemoryStorage = (): SafeStorage => {
  const store = new Map<string, string>();

  return {
    getItem(key: string) {
      return store.has(key) ? store.get(key)! : null;
    },
    setItem(key: string, value: string) {
      store.set(key, value);
    },
    removeItem(key: string) {
      store.delete(key);
    },
    clear() {
      store.clear();
    },
  };
};

const createSafeStorage = (getStorage: () => Storage | null | undefined): SafeStorage => {
  const memory = createMemoryStorage();
  let availableStorage: Storage | null | undefined;
  let disabled = false;

  const resolveStorage = () => {
    if (disabled) {
      return null;
    }

    if (availableStorage) {
      return availableStorage;
    }

    try {
      const storage = getStorage();
      if (!storage) {
        disabled = true;
        return null;
      }

      const testKey = '__safe_storage_test__';
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);

      availableStorage = storage;
      return availableStorage;
    } catch {
      disabled = true;
      return null;
    }
  };

  return {
    getItem(key: string) {
      const storage = resolveStorage();
      if (storage) {
        try {
          return storage.getItem(key);
        } catch {
          disabled = true;
        }
      }

      return memory.getItem(key);
    },
    setItem(key: string, value: string) {
      const storage = resolveStorage();
      if (storage) {
        try {
          storage.setItem(key, value);
          return;
        } catch {
          disabled = true;
        }
      }

      memory.setItem(key, value);
    },
    removeItem(key: string) {
      const storage = resolveStorage();
      if (storage) {
        try {
          storage.removeItem(key);
          return;
        } catch {
          disabled = true;
        }
      }

      memory.removeItem(key);
    },
    clear() {
      const storage = resolveStorage();
      if (storage) {
        try {
          storage.clear();
          return;
        } catch {
          disabled = true;
        }
      }

      memory.clear();
    },
  };
};

const getWindowStorage = <T extends 'localStorage' | 'sessionStorage'>(key: T) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window[key];
  } catch {
    return null;
  }
};

export const safeLocalStorage = createSafeStorage(() => getWindowStorage('localStorage'));
export const safeSessionStorage = createSafeStorage(() => getWindowStorage('sessionStorage'));
